import { useEffect, useState } from "react";
import type Competence from "constants/Competence";
import type { Transfert } from "models";
import type { ContactMetier, TypeContactMetier } from "models/ContactsMetiers";
import { metiersService } from "services";
import type { ContactMetierFilters } from "services/contactsMetiersService";
import { contactsMetiersService } from "services/contactsMetiersService";
import { createSwrConfiguration, metiersUtils } from "utils";
import { useErrorHandler } from "utils/errorHandling";
import useSWR from "swr";
import { timeUtil } from "@sdeapps/react-core";

interface UseContactsMetiersReturn {
  contactsMetiers: Array<ContactMetier>;
  isLoading: boolean;
}

export interface UseContactsMetiersParams {
  $type: TypeContactMetier;
  collectiviteId: string;
  transferts: Array<Transfert>;
  competences: Array<Competence>;
  filter?: Partial<ContactMetierFilters>;
}

/**
 * Custom Hook qui simplifie la récupération et la préparation des Contacts Métiers d'une Commune.
 * @returns UseContactsMetiersReturn
 */
export function useContactsMetiers({
  $type,
  collectiviteId,
  transferts,
  competences,
  filter,
}: UseContactsMetiersParams): UseContactsMetiersReturn {
  const [contactsMetiers, setContactsMetiers] = useState<Array<ContactMetier>>([]);
  const [contactsMetiersFiltres, setContactsMetiersFiltres] = useState<Array<ContactMetier>>([]);
  const { data: referentielMetiers, isLoading: isMetiersLoading } = useSWR(
    `metiers`,
    metiersService.getAll,
    createSwrConfiguration({ dedupingInterval: 1000 * 60 * 60 })
  );

  const { catchErrors: catchCometError, isLoading: isCometLoading } = useErrorHandler({
    dontThrow: true,
    defaultIsLoading: false,
  });

  useEffect(() => {
    if (
      collectiviteId != null &&
      // le filtre vient de l'argument filtre du hook pour un périmètre
      ($type !== "perimetre" || filter != null) &&
      // les transferts ne sont nécessaires que pour une commune
      (($type === "commune" && transferts.length !== 0) || $type !== "commune")
    ) {
      void catchCometError(async () => {
        let _filter: Partial<ContactMetierFilters> = { ...filter };
        switch ($type) {
          case "commune": {
            const perimetresIds = new Set<string>();
            const territoiresIds = new Set<string>();
            transferts.forEach((t) => {
              perimetresIds.add(t.perimetre.id);
              territoiresIds.add(t.perimetre.territoire.id);
            });
            _filter = {
              communesId: [collectiviteId],
              perimetresId: Array.from(perimetresIds),
              territoiresId: Array.from(territoiresIds),
              ..._filter,
            };
            break;
          }
          case "commune-associee-deleguee":
            _filter = { cadsId: [collectiviteId], ..._filter };
            break;
          case "perimetre":
            // Pour ce cas spécifiquement, le filtre vient de l'argument du hook
            break;
          case "territoire":
            _filter = { territoiresId: [collectiviteId], ..._filter };
            break;
        }

        const _contactsMetiers = (await contactsMetiersService.getByFilter(_filter)).values;

        setContactsMetiers(_contactsMetiers);
        // Bricolage pour éviter le FOICM (flash of inexistent Contacts Métiers)
        // afin d'assurer que `contactsMetiers` soit initialisé avant que `isLoading` devienne false
        await timeUtil.sleep(1);
      });
    } else {
      setContactsMetiers([]);
      setContactsMetiersFiltres([]);
    }
  }, [catchCometError, transferts, collectiviteId, $type, filter]);

  useEffect(() => {
    if (
      collectiviteId != null &&
      contactsMetiers.length > 0 &&
      referentielMetiers != null &&
      referentielMetiers.length > 0 &&
      competences.length > 0 &&
      (($type === "commune" && transferts.length !== 0) || $type !== "commune")
    ) {
      const contactsMetiersFiltres = metiersUtils.getContactMetiersToDisplay(
        $type,
        { referentielMetiers, contactsMetiers },
        { competences, transferts }
      );

      const inclutHorsCompetenceSdea = $type === "commune" || $type === "commune-associee-deleguee";
      const result = metiersUtils.getContactsMetiersGroupedByMetier(
        contactsMetiersFiltres,
        inclutHorsCompetenceSdea
      );
      setContactsMetiersFiltres(result);
    }
  }, [$type, collectiviteId, competences, contactsMetiers, referentielMetiers, transferts]);

  return { contactsMetiers: contactsMetiersFiltres, isLoading: isMetiersLoading || isCometLoading };
}
